/**
 * Massima Milano — robust autoplay for videos inside the product gallery.
 *
 * WHY THIS EXISTS (root cause, see product-gallery.liquid comment too):
 * The theme's own <video-media> element only calls video.play() when the
 * carousel reports a slide "settle" (see onMediaSettle in theme.js) or when
 * it has an `autoplay` attribute (which product-gallery.liquid deliberately
 * never sets — see its own comment). If that play() call is rejected with
 * NotAllowedError — which happens whenever `video.muted` isn't reliably
 * `true` as a *property* at the exact moment play() runs, not just present
 * as an HTML attribute — theme.js's own catch handler falls back to
 * `video.controls = true`, which is the "gray screen with a Play button"
 * merchants see. This script removes the guesswork: it owns play()/pause()
 * for gallery videos directly, forcing the muted/playsInline properties
 * immediately before every attempt, on every browser, every time.
 *
 * Scope: ONLY <video> elements inside `.product-gallery__media` (the
 * product page gallery, on every product). Never touches hero, editorial,
 * blog or YouTube/Vimeo embeds elsewhere on the site.
 */
(function () {
  if (window.__mmGalleryVideoAutoplayInit) return;
  window.__mmGalleryVideoAutoplayInit = true;

  var SELECTOR = ".product-gallery__media video";
  var VISIBLE_RATIO = 0.6;
  var observed = new WeakSet();
  var intersectionObserver = null;

  function primeVideo(video) {
    // Belt-and-suspenders: force every relevant property, not just the
    // matching HTML attributes — this is the actual fix for the Safari
    // NotAllowedError chain described above.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;
    video.controls = false;
    video.removeAttribute("controls");
    try {
      video.disablePictureInPicture = true;
    } catch (e) {
      /* not supported, ignore */
    }
    try {
      video.disableRemotePlayback = true;
    } catch (e) {
      /* not supported, ignore */
    }
  }

  function attemptPlay(video) {
    primeVideo(video);
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(function () {
        // Autoplay can still be legitimately blocked (e.g. Data Saver mode) —
        // fail silently rather than showing the theme's controls fallback or
        // logging noise. The video simply stays on its poster/first frame.
      });
    }
  }

  function attemptPause(video) {
    if (!video.paused) video.pause();
  }

  function ensureObserved(video) {
    if (observed.has(video)) return;
    observed.add(video);
    primeVideo(video);

    if (video.readyState === 0) {
      // Force metadata to load so the first frame is ready to paint instead
      // of leaving a blank box behind the (now-hidden) poster.
      video.preload = "metadata";
    }

    intersectionObserver.observe(video);
  }

  function handleIntersections(entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO) {
        attemptPlay(entry.target);
      } else {
        attemptPause(entry.target);
      }
    });
  }

  function scan(root) {
    var nodes = root.querySelectorAll ? root.querySelectorAll(SELECTOR) : [];
    for (var i = 0; i < nodes.length; i++) ensureObserved(nodes[i]);
  }

  function init() {
    intersectionObserver = new IntersectionObserver(handleIntersections, {
      threshold: [0, VISIBLE_RATIO],
    });

    scan(document);

    // Covers: language switch re-rendering a slide, the translate app's
    // per-locale media swap, and any future dynamic insert into the gallery —
    // without ever touching video elements outside the product gallery.
    var mutationObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            if (node.matches && node.matches(SELECTOR)) ensureObserved(node);
            if (node.querySelectorAll) scan(node);
          });
        }
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
